import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ClipboardPaste, Code, List, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import FormField from '@/components/ui/form-field';
import FormInput from '@/components/ui/form-input';
import { Textarea } from '@/components/ui/textarea';
import { getIconPixels } from '@/theme/size-tokens';
import type { JSONObject, JSONValue } from '@/types/shared/json-schema';

type KeyValueFieldProps = {
  label: string;
  value: JSONObject | string;
  onChange: (value: JSONObject | string) => void;
  required?: boolean;
  disabled?: boolean;
  labelSuffix?: ReactNode;
  error?: string;
};

type KVEntry = { id: string; key: string; value: string };

let nextId = 0;
function makeId(): string {
  return `kv-${++nextId}`;
}

function isJSONObject(value: unknown): value is JSONObject {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function parseObjectText(text: string): JSONObject | null {
  if (text.trim() === '') {
    return {};
  }

  try {
    const parsed = JSON.parse(text);
    return isJSONObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function isPrimitiveJSONValue(
  value: JSONValue
): value is string | number | boolean | null {
  return (
    value == null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

function isFlatJSONObject(value: JSONObject): boolean {
  return Object.values(value).every(isPrimitiveJSONValue);
}

function shouldQuoteStringValue(value: string): boolean {
  if (value === '' || value.trim() !== value) {
    return true;
  }

  try {
    const parsed = JSON.parse(value);
    return isPrimitiveJSONValue(parsed) && typeof parsed !== 'string';
  } catch {
    return false;
  }
}

function displayValue(value: JSONValue): string {
  if (typeof value === 'string') {
    return shouldQuoteStringValue(value) ? JSON.stringify(value) : value;
  }

  return JSON.stringify(value);
}

function parseVisualValue(value: string): JSONValue {
  const trimmed = value.trim();

  if (trimmed === '') {
    return '';
  }

  try {
    const parsed = JSON.parse(trimmed);
    return isPrimitiveJSONValue(parsed) ? parsed : value;
  } catch {
    return value;
  }
}

function objectToEntries(record: JSONObject): KVEntry[] {
  const entries = Object.entries(record).map(([key, value]) => ({
    id: makeId(),
    key,
    value: displayValue(value),
  }));
  return entries.length > 0 ? entries : [{ id: makeId(), key: '', value: '' }];
}

function entriesToObject(entries: KVEntry[]): JSONObject {
  const record: JSONObject = {};
  for (const entry of entries) {
    const key = entry.key.trim();
    if (key) {
      record[key] = parseVisualValue(entry.value);
    }
  }
  return record;
}

function serializeFieldValue(value: JSONObject | string): string {
  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value, null, 2);
}

function defaultEntries(): KVEntry[] {
  return [{ id: makeId(), key: '', value: '' }];
}

function stableFieldValue(value: JSONObject | string): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

export default function KeyValueField({
  label,
  value,
  onChange,
  required,
  disabled,
  labelSuffix,
  error,
}: KeyValueFieldProps) {
  const [mode, setMode] = useState<'visual' | 'json'>(() =>
    typeof value === 'string' && value.trim() !== '' ? 'json' : 'visual'
  );
  const [entries, setEntries] = useState<KVEntry[]>(() =>
    isJSONObject(value) && isFlatJSONObject(value)
      ? objectToEntries(value)
      : defaultEntries()
  );
  const [jsonText, setJsonText] = useState(() => serializeFieldValue(value));
  const lastEmittedValueRef = useRef<string | null>(null);

  useEffect(() => {
    const nextStableValue = stableFieldValue(value);
    if (lastEmittedValueRef.current === nextStableValue) {
      lastEmittedValueRef.current = null;
      return;
    }

    setJsonText(serializeFieldValue(value));

    if (typeof value === 'string') {
      if (value.trim() === '') {
        setEntries(defaultEntries());
        setMode('visual');
        return;
      }

      const parsed = parseObjectText(value);
      setEntries(
        parsed && isFlatJSONObject(parsed)
          ? objectToEntries(parsed)
          : defaultEntries()
      );
      setMode('json');
      return;
    }

    if (isJSONObject(value) && isFlatJSONObject(value)) {
      setEntries(objectToEntries(value));
      return;
    }

    setEntries(defaultEntries());
    setMode('json');
  }, [value]);

  const emitValue = (nextValue: JSONObject | string) => {
    lastEmittedValueRef.current = stableFieldValue(nextValue);
    onChange(nextValue);
  };

  const handleKeyChange = (id: string, newKey: string) => {
    setEntries((prev) => {
      const next = prev.map((e) => (e.id === id ? { ...e, key: newKey } : e));
      const nextValue = entriesToObject(next);
      setJsonText(JSON.stringify(nextValue, null, 2));
      emitValue(nextValue);
      return next;
    });
  };

  const handleValueChange = (id: string, newValue: string) => {
    setEntries((prev) => {
      const next = prev.map((e) =>
        e.id === id ? { ...e, value: newValue } : e
      );
      const nextValue = entriesToObject(next);
      setJsonText(JSON.stringify(nextValue, null, 2));
      emitValue(nextValue);
      return next;
    });
  };

  const handleAdd = () => {
    setEntries((prev) => {
      const next = [...prev, { id: makeId(), key: '', value: '' }];
      const nextValue = entriesToObject(next);
      setJsonText(JSON.stringify(nextValue, null, 2));
      emitValue(nextValue);
      return next;
    });
  };

  const handleRemove = (id: string) => {
    setEntries((prev) => {
      const filtered = prev.filter((e) => e.id !== id);
      const next = filtered.length > 0 ? filtered : defaultEntries();
      const nextValue = entriesToObject(next);
      setJsonText(JSON.stringify(nextValue, null, 2));
      emitValue(nextValue);
      return next;
    });
  };

  const switchToJson = () => {
    setJsonText(JSON.stringify(entriesToObject(entries), null, 2));
    setMode('json');
  };

  const switchToVisual = () => {
    const parsed = parseObjectText(jsonText);

    if (!parsed) {
      toast.error('Invalid JSON — fix errors before switching to visual mode.');
      return;
    }

    if (!isFlatJSONObject(parsed)) {
      toast.error(
        'Visual mode only supports flat primitive values. Keep nested values in JSON mode.'
      );
      return;
    }

    const next = objectToEntries(parsed);
    setEntries(next);
    emitValue(parsed);
    setMode('visual');
  };

  const handleJsonChange = (text: string) => {
    setJsonText(text);
    emitValue(text);
  };

  const handlePasteJson = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = parseObjectText(text);
      if (!parsed) {
        toast.error('Clipboard does not contain a JSON object.');
        return;
      }

      setJsonText(JSON.stringify(parsed, null, 2));

      if (isFlatJSONObject(parsed) && mode === 'visual') {
        const next = objectToEntries(parsed);
        setEntries(next);
        emitValue(parsed);
      } else {
        setMode('json');
        emitValue(JSON.stringify(parsed, null, 2));
      }

      toast.success('Pasted JSON data.');
    } catch {
      toast.error('Could not read clipboard or invalid JSON.');
    }
  };

  const jsonTextareaRef = useRef<HTMLTextAreaElement>(null);

  const BRACKET_PAIRS: Record<string, string> = {
    '{': '}',
    '[': ']',
    '"': '"',
  };

  const CLOSE_CHARS = new Set(['}', ']', '"']);

  const handleJsonKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const ta = jsonTextareaRef.current;
    if (!ta) return;

    const { selectionStart, selectionEnd, value: text } = ta;

    // Auto-close brackets and quotes
    const closer = BRACKET_PAIRS[e.key];
    if (closer) {
      // For quotes, skip auto-close if already inside a string (simple heuristic)
      if (e.key === '"') {
        const charAfter = text[selectionStart];
        if (charAfter === '"') {
          // Skip over existing closing quote
          e.preventDefault();
          ta.selectionStart = ta.selectionEnd = selectionStart + 1;
          handleJsonChange(text);
          return;
        }
      }

      e.preventDefault();
      const selected = text.slice(selectionStart, selectionEnd);
      const inserted = e.key + selected + closer;
      const next =
        text.slice(0, selectionStart) + inserted + text.slice(selectionEnd);
      handleJsonChange(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = selectionStart + 1;
      });
      return;
    }

    // Skip over closing chars if the next char matches
    if (CLOSE_CHARS.has(e.key) && text[selectionStart] === e.key) {
      e.preventDefault();
      ta.selectionStart = ta.selectionEnd = selectionStart + 1;
      return;
    }

    // Enter: auto-indent
    if (e.key === 'Enter') {
      e.preventDefault();
      const before = text.slice(0, selectionStart);
      const after = text.slice(selectionEnd);
      const currentLine = before.split('\n').pop() ?? '';
      const indent = currentLine.match(/^(\s*)/)?.[1] ?? '';
      const charBefore = before[before.length - 1];
      const charAfter = after[0];

      let insertion: string;
      let cursorOffset: number;

      if (
        (charBefore === '{' && charAfter === '}') ||
        (charBefore === '[' && charAfter === ']')
      ) {
        // Expand bracket pair with indented cursor between them
        const innerIndent = indent + '  ';
        insertion = '\n' + innerIndent + '\n' + indent;
        cursorOffset = 1 + innerIndent.length;
      } else if (charBefore === '{' || charBefore === '[') {
        const innerIndent = indent + '  ';
        insertion = '\n' + innerIndent;
        cursorOffset = 1 + innerIndent.length;
      } else {
        insertion = '\n' + indent;
        cursorOffset = 1 + indent.length;
      }

      const next = before + insertion + after;
      handleJsonChange(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = selectionStart + cursorOffset;
      });
      return;
    }

    // Tab: insert 2 spaces
    if (e.key === 'Tab') {
      e.preventDefault();
      const next =
        text.slice(0, selectionStart) + '  ' + text.slice(selectionEnd);
      handleJsonChange(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = selectionStart + 2;
      });
    }
  };

  return (
    <FormField
      label={label}
      labelSuffix={labelSuffix}
      error={error}
      required={required}
      disabled={disabled}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-end gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={handlePasteJson}
            disabled={disabled}
            aria-label="Paste JSON from clipboard"
            title="Paste JSON"
          >
            <ClipboardPaste size={getIconPixels('xs')} />
          </Button>
          <Button
            type="button"
            variant={mode === 'visual' ? 'secondary' : 'ghost'}
            size="icon-sm"
            onClick={mode === 'json' ? switchToVisual : undefined}
            disabled={disabled || mode === 'visual'}
            aria-label="Visual editor"
            title="Visual editor"
          >
            <List size={getIconPixels('xs')} />
          </Button>
          <Button
            type="button"
            variant={mode === 'json' ? 'secondary' : 'ghost'}
            size="icon-sm"
            onClick={mode === 'visual' ? switchToJson : undefined}
            disabled={disabled || mode === 'json'}
            aria-label="JSON editor"
            title="JSON editor"
          >
            <Code size={getIconPixels('xs')} />
          </Button>
        </div>

        {mode === 'visual' ? (
          <div className="border-border flex flex-col gap-2 rounded-md border p-3">
            {entries.map((entry) => (
              <div key={entry.id} className="flex items-center gap-2">
                <FormInput
                  placeholder="Key"
                  value={entry.key}
                  onChange={(e) => handleKeyChange(entry.id, e.target.value)}
                  disabled={disabled}
                  className="flex-1"
                  autoComplete="off"
                />
                <FormInput
                  placeholder="Value"
                  value={entry.value}
                  onChange={(e) => handleValueChange(entry.id, e.target.value)}
                  disabled={disabled}
                  className="flex-1"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => handleRemove(entry.id)}
                  disabled={disabled || entries.length <= 1}
                  className="text-muted-foreground hover:text-foreground disabled:text-muted-foreground/40 shrink-0 p-1 transition-colors disabled:cursor-not-allowed"
                  aria-label="Remove entry"
                >
                  <X size={getIconPixels('sm')} />
                </button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAdd}
              disabled={disabled}
              className="mt-1 w-fit"
            >
              <Plus size={getIconPixels('xs')} />
              Add entry
            </Button>
          </div>
        ) : (
          <Textarea
            ref={jsonTextareaRef as RefObject<HTMLTextAreaElement>}
            value={jsonText}
            onChange={(e) => handleJsonChange(e.target.value)}
            onKeyDown={handleJsonKeyDown}
            disabled={disabled}
            placeholder='{ "key": "value" }'
            className="font-mono text-xs"
            rows={6}
          />
        )}
      </div>
    </FormField>
  );
}
